import { ResizeObserver } from "@juggle/resize-observer";
import classnames from "classnames";
import _ from "lodash";
import React, { useEffect, useRef } from "react";
import { isMobileOnly, isTablet } from 'react-device-detect';
import ReactMarkdown from "react-markdown";
import { createGlobalStyle } from "styled-components/macro";
import { drawnBulletMarkerStyles } from "./components/TiptapDocumentSB/EditorContainer/editorContentStyles";
import { Link, useLocation } from "react-router-dom";
import useMeasure from "react-use-measure";
import Cookies from "universal-cookie";
import Auth from "./Auth";
import { useAdminAccessStore } from "./stores/adminAccessStore";
import { MobileDeviceWarning } from "./components/MobileDeviceWarning/index.js";
import SudowriteLogo from "./components/SudowriteLogo";
import firebase from "./Firebase.js";
import mixpanel from "./MixpanelConfiguration";
// SW-8538 Phase B: SubscribeButton + SubscribeLink moved to a .tsx file so
// Vitest can import them. Re-exported below for back-compat with existing
// `import { SubscribeButton } from "src/Utils"` call sites.
export { SubscribeButton, SubscribeLink } from "./components/SubscribeButton";

const diff = require("diff");

const ListContext = React.createContext({ type: null, index: 0 });

/* Bullet lists render with the editor's CSS-drawn markers (see
   MarkdownBulletListStyles below) instead of literal `• ` text. Ordered
   lists keep literal `1. ` markers as selectable text: the manuscript
   schema has no orderedList node, so copy/insert paths rely on the numbers
   living in the text itself (copyUtils strips/re-adds them). */
const MarkdownBulletListStyles = createGlobalStyle`
  ${drawnBulletMarkerStyles("", { listSelector: "ul.sw-drawn-bullets", itemTarget: "> li" })}

  /* Gutter for the hanging marker (editor uses margin-left: 1em; card CSS
     may widen this). Tight items like the editor; nested lists sit close
     to their parent bullet. */
  ul.sw-drawn-bullets {
    margin-left: 1em;
  }

  ul.sw-drawn-bullets ul.sw-drawn-bullets {
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* Loose markdown items wrap their content in <p>; keep the first one on
     the marker's line (the marker ::before sits on the <li>). */
  ul.sw-drawn-bullets > li > p:first-child {
    display: inline;
    margin: 0;
  }
`;

export function markdownFormatter(string) {
  if (!string) return null;

  // Custom list item component (must start with uppercase to use hooks)
  const ListItem = ({ children, ...props }) => {
    const context = React.useContext(ListContext);
    const marker = context.type === 'ordered' ? `${context.index}. ` : '';
    return <li {...props}>{marker}{children}</li>;
  };

  // Custom components to render ordered-list markers as actual selectable text
  const components = {
    ol: ({ children, ...props }) => {
      let counter = 0;
      return (
        <ol className='pb-2' {...props}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              counter++;
              return (
                <ListContext.Provider value={{ type: 'ordered', index: counter }}>
                  {child}
                </ListContext.Provider>
              );
            }
            return child;
          })}
        </ol>
      );
    },
    ul: ({ children, ...props }) => {
      return (
        <ul className='pb-2 sw-drawn-bullets' {...props}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return (
                <ListContext.Provider value={{ type: 'unordered', index: 0 }}>
                  {child}
                </ListContext.Provider>
              );
            }
            return child;
          })}
        </ul>
      );
    },
    li: ListItem
  };

  return (
    <>
      <MarkdownBulletListStyles />
      <ReactMarkdown components={components}>{string}</ReactMarkdown>
    </>
  );
}

export function chooseRandom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

// Combining
// https://stackoverflow.com/questions/57765958/how-to-detect-ipad-and-ipad-os-version-in-ios-13-and-up
// + https://dev.to/timhuang/a-simple-way-to-detect-if-browser-is-on-a-mobile-device-with-javascript-44j3
export function isMobileLegacy() {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    return true;
  } else {
    let ipad = navigator.userAgent.toLowerCase().indexOf("ipad") !== -1;

    if (!ipad && navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2) {
      return true;
    }

    return ipad;
  }
}

export function makeId(length) {
  var result = "";
  var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function getOS() {
  if (window.navigator.appVersion.indexOf("Win") != -1) return "windows";
  if (window.navigator.appVersion.indexOf("Mac") != -1) return "mac";
  return "windows";
}

export function getCoords(elem) {
  // crossbrowser version
  var box = elem.getBoundingClientRect();

  var body = document.body;
  var docEl = document.documentElement;

  var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  var clientTop = docEl.clientTop || body.clientTop || 0;
  var clientLeft = docEl.clientLeft || body.clientLeft || 0;

  var top = box.top + scrollTop - clientTop;
  var left = box.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
}

export const SAMPLE_STORY = `How Did it Feel to be Eaten?

    "I was an elderberry," I announced, glowing with pride.
    "How did it feel to be eaten?" he asked.
    It seemed an odd question, but a response came unbidden, so I voiced it. "It was an honor." My words surprised me, but they felt true.
    "The Queen of England ate me," I added.
    How did I know this? Who was speaking to me? My cheeks flushed. I didn't feel like a berry. Did berries feel embarrassed?
    "I didn't know she was the Queen at the time," I admitted.
    "Yes," agreed the man whom I could not see and did not know.
    "Let's try another."
#
    I felt immensely powerful. I sparkled in the sun. The land beneath me rose, I stood, and I felt a caress on my shoulder. A child. We danced. I rolled, crested, and rumbled; she banked and cut on her board, gliding gracefully along me, her speed blowing droplets of me right off her wetsuit. We became one.
    We reached the shore, and I crumbled, making room for others like me, and others like her.
    That was a short one.
#
    I awakened again.
    "Do you know where you are now?" asked the man with a warm smile.
    To my surprise, I did.
`;

export async function sendEmailSignIn(email, { next } = {}) {
  return Auth.fetchWithIdToken("/api/login/sign-in-link/send", {
    email,
    next,
  });
}

/**
 * Returns the required radius for a circle that ensures that rectangles evenly distributed
 * along it would not overlap
 */
export const getCircleRadius = (width, height, N) => {
  const angle = 360 / N;
  const halfAngle = angle / 2;
  const radius = Math.max(width, height) / (2 * Math.sin(halfAngle * (Math.PI / 180)));
  return radius;
};

/**
 * Gets points along a circle evenly distributed
 * @param {Number} r Radius
 * @param {Number} x Origin x
 * @param {Number} y Origin y
 * @param {Number} N Number of points
 * @returns
 */
export const getPointsOnCircle = (r, x, y, N) => {
  const points = [];
  for (let i = 0; i < N; i++) {
    const theta = (i / N) * 2 * Math.PI - Math.PI / 2; // start at top of circle
    const pointX = x + r * Math.cos(theta);
    const pointY = y + r * Math.sin(theta);
    points.push([pointX, pointY]);
  }
  return points;
};

/**
 * Gets points along an ellipse evenly distributed
 * @param {Number} a Horizontal radius
 * @param {Number} b Vertical radius
 * @param {Number} x Origin x
 * @param {Number} y Origin y
 * @param {Number} N Number of points
 * @returns {Array} Array of points in the format of [x, y]
 */
export const getPointsOnEllipse = (a, b, x, y, N) => {
  const points = [];
  for (let i = 0; i < N; i++) {
    const theta = (i / N) * 2 * Math.PI - Math.PI / 2; // start at top of ellipse
    const pointX = x + a * Math.cos(theta);
    const pointY = y + b * Math.sin(theta);
    points.push([pointX, pointY]);
  }
  return points;
};

/**
 * Finds the object given a matcher
 * @param {Object} obj
 * @param {function} matcher
 * @returns
 */
export const findVal = (obj, matcher) => {
  for (let key in obj) {
    if (matcher(obj[key])) {
      return obj[key];
    }
  }
  return undefined;
};

// Sends an event to be saved in firestore and mixpanel
// accepts events as an array of objects:
// { eventType: string, cardId: string, path: [key, index] }
export async function event(events) {
  if (!events.length) {
    // assume events is a single object
    events = [events];
  }

  // check
  for (let event of events) {
    if (!Array.isArray(event.path) || event.path.length != 2) {
      throw new Error("Path must of form [key, index]");
    }
    if (typeof event.cardId !== "string") {
      throw new Error("cardId must be a string");
    }
    if (typeof event.eventType !== "string") {
      throw new Error("eventType must be a string");
    }
  }

  try {
    await Auth.fetchWithIdToken("/api/event", { events: events });
  } catch (err) {
    console.log("Error sending events", events);
  }

  return true;
}

function getDefaultClientEventProperties() {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const appVersion = navigator.appVersion;
  const os = platform.includes("Win") ? "Windows" : platform.includes("Mac") ? "MacOS" : "Other";
  const osVersion = appVersion.match(/(Windows NT|Mac OS X) ([\d._]+)/)?.[2] || "unknown";
  const browser = userAgent.match(/(firefox|msie|trident|chrome|safari|opera)/i)?.[0] || "unknown";
  const browserVersion = userAgent.match(/(firefox|msie|trident|chrome|safari|opera)[\/\s]?([\d.]+)/i)?.[2] || "unknown";
  const deviceCategory = /iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent) ? "Tablet" : /Mobi|Android/i.test(userAgent) ? "Mobile" : "Desktop";
  const screenHeight = window.screen.height;
  const screenWidth = window.screen.width;
  const screenDpi = window.devicePixelRatio || 1;
  const currentUrl = window.location.href;
  const referrerUrl = document.referrer;

  return {
    os,
    os_version: osVersion,
    browser,
    browser_version: browserVersion,
    device_category: deviceCategory,
    screen_height: screenHeight,
    screen_width: screenWidth,
    screen_dpi: screenDpi,
    current_url: currentUrl,
    referrer_url: referrerUrl,
  };
}

// Main call to send an app event which is federated to c.io, mixpanel, and firestore
// This also updates the user metadata in c.io and mixpanel
export async function sendAppEvent(name, props = {}, { retries = 0 } = {}) {
  props.clientVersion = window.sudowriteClientVersion || "unknown";
  const defaultProps = getDefaultClientEventProperties();
  const sessionRecordingProperties = mixpanel.get_session_recording_properties();
  props = { ...defaultProps, ...props, ...sessionRecordingProperties };

  // Get clientId from cookie to make sure it's included in the request
  const cookies = new Cookies();
  const clientId = cookies.get("client_id");

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Deliberately independent of the generation AbortController.
      return await Auth.fetchWithIdToken("/api/app-events/", { name, props, clientId });
    } catch {
      if (attempt < retries) await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
    }
  }
}

export async function sendInvocationEvent(invocationId, eventType, value) {
  if (!invocationId || !eventType) return;
  Auth.fetchWithIdToken("/api/invocationEvent", {
    events: [
      {
        invocationId,
        eventType,
        value,
      },
    ],
  });
}
// Dedicated function for logging admin view events to the database
export async function logAdminView(path) {
  const user = firebase.auth().currentUser;
  const access = useAdminAccessStore.getState();
  // Auth can switch before the admin page unmounts. Do not send an admin
  // request with a customer's session or another identity's cached permissions.
  if (!user || access.authUid !== user.uid || !access.permissions.includes("admin.access")) return;
  // Skip logging for the admin logs page itself
  if (path === "/admin/logs") {
    return;
  }

  Auth.fetchWithIdToken("/api/admin/log-view", {
    user,
    path: path,
  }).catch((e) => {
    console.log("Failed to log admin view:", e);
  });
}

export function BackToDocumentButton(props) {
  return (
    <Link
      className="back"
      to={`/app/project/${props.projectId}/#doc=${props.documentId}`}
    >
      &larr; Back
    </Link>
  );
}

export function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const SOURCES = [
  "guidedWormhole",
  "character",
  "twist",
  "description",
  "dialogue",
  "multiverse",
  "summary",
  "expand",
  "whatIf",
  "analyze",
  "poem",
  "rewrite",
  "myVoiceRewrite",
  "brainstorm",
  "tweetstorm",
  "custom",
  "visualize",
  "command",
  "outline",
  "visualizeCanvas",
  "shrinkRay",
  "canvasOutline",
  "canvasOutlineCard",
  "canvasCard",
  "plugin",
  "chapterGenerator",
  "quickEditRestore",
];

export function trimEndSpaces(str) {
  return str.replace(/\s+$/g, "");
}

// Matches CJK Unified Ideographs, Extensions, Hiragana, Katakana, Hangul Syllables
const CJK_REGEX = /[\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\uf900-\ufaff\u{20000}-\u{2a6df}\u{2a700}-\u{2b73f}\u{2b740}-\u{2b81f}]/u;

function splitCJKAware(word) {
  if (!CJK_REGEX.test(word)) return [word];
  const results = [];
  let nonCJKBuffer = '';
  for (const char of word) {
    if (CJK_REGEX.test(char)) {
      if (nonCJKBuffer) {
        results.push(nonCJKBuffer);
        nonCJKBuffer = '';
      }
      results.push(char);
    } else {
      nonCJKBuffer += char;
    }
  }
  if (nonCJKBuffer) results.push(nonCJKBuffer);
  return results;
}

// Avoid `\b` + Unicode character class: Firefox can throw InternalError ("too much recursion").
// Lookarounds mirror ASCII `\b` around delimiter runs (same split behavior as the old regex).
const WORD_DELIMITER_REGEX = /(?<=[A-Za-z0-9_])[^-'’\p{L}0-9_]+(?=[A-Za-z0-9_])/u;
const WORD_DELIMITER_FALLBACK_REGEX = /\s+/;

function isRegexStackError(error) {
  if (error instanceof RangeError) return true;
  if (error && error.name === "InternalError") return true;
  const message = error && error.message;
  return typeof message === "string" && /too much recursion|maximum call stack/i.test(message);
}

function splitTextOnWordDelimiters(text) {
  try {
    return text.split(WORD_DELIMITER_REGEX);
  } catch (error) {
    if (!isRegexStackError(error)) {
      throw error;
    }
    return text.split(WORD_DELIMITER_FALLBACK_REGEX);
  }
}

export function splitWords(text) {
  if (typeof text !== "string") {
    return [];
  }

  text = text.trim();
  if (text.length === 0) return [];

  const wordArray = splitTextOnWordDelimiters(text);

  // catch cases not covered by regex
  let newArray = wordArray.map((word) => {
    if (word.indexOf(" - ") > -1) {
      return word.split(" - ");
    } else if (word.indexOf("--") > -1) {
      return word.split("--");
    } else if (word.indexOf(" ") > -1) {
      return word.split(" ");
    } else {
      return word;
    }
  });

  // flatten array
  newArray = newArray.flat();
  // Split CJK characters into individual words
  newArray = newArray.flatMap(splitCJKAware);

  return newArray;
}

export function countWords(text) {
  return splitWords(text).length;
}

// This function has O(n) time efficiency in worst case scenario
export const truncateWords = (maxWords, text) => {
  if (maxWords <= 0) {
    return "";
  }

  const wordsArr = splitWords(text);

  if (wordsArr.length <= maxWords) {
    return text;
  }

  const lastWord = wordsArr[maxWords - 1];
  // Empty "words" from splitWords() make indexOf("") always match at 0 with zero advance → infinite loop.
  if (lastWord.length === 0) {
    return text;
  }

  const lastIdx = text.indexOf(lastWord) + lastWord.length;

  let selection = text.slice(0, lastIdx);
  let prevLastIdx = lastIdx;

  // if the last word occurs more than once in the desired selection
  // we need to make sure we grab the whole selection
  while (countWords(selection) < maxWords) {
    let currLastIdx = text.slice(prevLastIdx).indexOf(lastWord);

    if (currLastIdx <= -1) {
      break;
    }
    currLastIdx += lastWord.length;

    if (currLastIdx === 0) {
      break;
    }

    selection += text.slice(prevLastIdx, prevLastIdx + currLastIdx);
    const nextPrevLastIdx = prevLastIdx + currLastIdx;
    if (nextPrevLastIdx <= prevLastIdx) {
      break;
    }
    prevLastIdx = nextPrevLastIdx;

    // guard against infinite loops
    if (prevLastIdx >= text.length) {
      break;
    }
  }

  return selection;
};

export const keepWordsFromEnd = (maxWords, text) => {
  const wordsArr = splitWords(text);

  if (wordsArr.length <= maxWords) {
    return text;
  }

  let truncatedText = text;
  let wordIndex = 0;

  for (let i = 0; i < wordsArr.length - maxWords; i++) {
    wordIndex = truncatedText.indexOf(wordsArr[i], wordIndex) + wordsArr[i].length;
  }

  return truncatedText.slice(truncatedText.indexOf(wordsArr[wordsArr.length - maxWords], wordIndex));
};

export const DISCORD_LINK = "https://discord.gg/z9b3N4gyUM";

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Note: Deprecated! Use the common component instead
export function Tip(props) {
  let right = false;
  if (props.position == "right") {
    right = true;
  }

  if (props.style) {
  }

  return (
    <div
      className={classnames({ tip: true, right: right })}
      style={props.style || {}}
    >
      <span>{props.children}</span>
      <div className="arrow"></div>
    </div>
  );
}

export const parseCommands = (commandText) => {
  const lines = commandText.trim().split("\n");

  // Find the first line that starts with one or more digits followed by a period
  // This will serve as the start of our beats and the end of our header (if present)
  const firstBeatIndex = lines.findIndex((line) => /^\d+\./.test(line.trim()));

  if (firstBeatIndex > -1) {
    // Everything before the first beat is considered the header
    // We join these lines back together with "\n" in case the header spans multiple lines
    const header = lines
      .slice(0, firstBeatIndex)
      .map((line) => line.trim())
      .join("\n");

    // Everything from the first beat onward is part of the beats
    // We trim each line and filter out any empty lines
    const beats = lines
      .slice(firstBeatIndex)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    return { header, beats };
  } else {
    const beats = lines.map((line) => line.trim()).filter((line) => line.length > 0);
    return { beats };
  }
};

export function roundOffTo(num, factor = 1) {
  const quotient = num / factor;
  const res = Math.round(quotient) * factor;
  return res;
}

export function setMetaThemeColor(color) {
  let metaThemeColor = document.querySelector("meta[name=theme-color]");
  if (!metaThemeColor) {
    metaThemeColor = document.createElement("meta");
    metaThemeColor.setAttribute("name", "theme-color");
    document.head?.appendChild(metaThemeColor);
  }

  if (!metaThemeColor.isConnected) {
    return;
  }

  metaThemeColor.setAttribute("content", color);
}

export function isMetaBrowser() {
  return /FB_IAB|FBAV|Instagram|IABMV/i.test(navigator.userAgent);
}

// Normal Distribution With Min, Max, Skew
// https://stackoverflow.com/questions/25582882/javascript-math-random-normal-distribution-gaussian-bell-curve
export function randn_bm(min, max, skew) {
  let u = 0,
    v = 0;
  while (u === 0) u = Math.random(); //Converting [0,1) to (0,1)
  while (v === 0) v = Math.random();
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);

  num = num / 10.0 + 0.5; // Translate to 0 -> 1
  if (num > 1 || num < 0) num = randn_bm(min, max, skew); // resample between 0 and 1 if out of range
  num = Math.pow(num, skew); // Skew
  num *= max - min; // Stretch to fill range
  num += min; // offset to min
  return num;
}

// Polls a condition and resolves when that condition is true
export function waitFor(conditionFunction, time) {
  const poll = (resolve) => {
    if (conditionFunction()) resolve();
    else setTimeout((_) => poll(resolve), time);
  };

  return new Promise(poll);
}

export class CenterBox extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    return (
      <div id="center-box">
        <div className="inner">
          {!this.props.noTitle && (
            <SudowriteLogo
              className="logo"
              size="xxx-large"
            />
          )}
          <div>{this.props.children}</div>
        </div>
      </div>
    );
  }
}

export function delimited(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function randomId(length) {
  var result = "";
  var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function startUrl() {
  return window.location.protocol + "//" + window.location.hostname + (window.location.port ? ":" + window.location.port : "");
}

export function hash(string) {
  var hash = 0,
    i,
    chr;
  for (i = 0; i < string.length; i++) {
    chr = string.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export const isMobileUserAgent = isMobileLegacy();

export function MobileWarning(props) {
  const [ref, { width }] = useMeasure({ polyfill: ResizeObserver });
  const mobileShown = useRef(false);
  const eventSent = useRef(false);

  useEffect(() => {
    if (width !== 0 && !eventSent.current) {
      mobileShown.current = true;
      sendAppEvent("Shown Mobile Warning", {
        isMobile: isMobileOnly,
      });
      eventSent.current = true;
    }
  }, [width]);

  if (isMobileOnly) return <MobileDeviceWarning />;

  let children = props.children;

  if (!children && isTablet) {
    children = "Sudowrite works best on browsers at least 900px wide. Please switch to a larger browser, or switch to landcape mode to use Sudowrite.";
  } else if (!children) {
    children = "Sudowrite works best on browsers at least 900px wide. Please switch to a larger browser, or zoom out to use Sudowrite.";
  }

  return (
    <div
      ref={ref}
      id="mobile-warning"
      className="p-4 text-lg"
    >
      <p>{children}</p>
    </div>
  );
}

export async function fetchJSON(url, params) {
  return await fetch(url, {
    headers: { "Content-Type": "application/json" },
    method: "POST",
    body: params ? JSON.stringify(params) : null,
  });
}

export function isObject(yourVariable) {
  return typeof yourVariable === "object" && yourVariable !== null && !Array.isArray(yourVariable);
}

export function formattedDiff(original, current) {
  // Split the original and current texts into paragraphs
  const originalParagraphs = original.split(/\n\n+/);
  const currentParagraphs = current.split(/\n\n+/);
  let combinedHtml = "<div>";

  // Iterate over the paragraphs, running diff on each pair
  for (let i = 0; i < Math.max(originalParagraphs.length, currentParagraphs.length); i++) {
    let originalPara = originalParagraphs[i] || "";
    let currentPara = currentParagraphs[i] || "";
    const difference = diff.diffWords(originalPara, currentPara);

    let paragraphHtml = "<div class='pt-3'>";
    for (let chunk of difference) {
      let { added, removed, value } = chunk;

      // Replace line breaks in the value with HTML line breaks
      let formattedValue = value.replace(/\n/g, "<br/><br/>");

      if (added) {
        paragraphHtml += `<span class='changed'>${formattedValue}</span>`;
      } else if (!removed) {
        paragraphHtml += formattedValue;
      }
    }
    paragraphHtml += "</div>";

    // Add the paragraph HTML to the combined HTML
    combinedHtml += paragraphHtml;
  }

  combinedHtml += "</div>";

  return <div dangerouslySetInnerHTML={{ __html: combinedHtml }} />;
}

export function getIn(obj, keys, fallback) {
  if (!keys.length) {
    return [obj];
  }

  const [firstKey, ...remainingKeys] = keys;

  if (typeof firstKey === "function") {
    // Filter the values.
    return Object.values(obj)
      .filter((child) => firstKey(child))
      .flatMap((child) => getIn(child, remainingKeys));
  } else if (firstKey === "*") {
    // Check all paths.
    return Object.values(obj).flatMap((child) => getIn(child, remainingKeys));
  } else if (Object.prototype.hasOwnProperty.call(obj, firstKey)) {
    return getIn(obj[firstKey], remainingKeys);
  }

  return fallback;
}

export const clamp = (num, min, max) => {
  return num <= min ? min : num >= max ? max : num;
};

export const truncateString = (str, maxLength) => {
  return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
};

// takes a string representation of user's theme and determines if that theme is a dark mode theme
export const isDarkMode = (theme) => {
  return theme === "amber" || theme === "forest" || theme === "aurora-borealis" || theme === "cauldron" || theme === "charcoal";
};

export function pluckDeep(obj, key) {
  let result = [];

  function traverse(current) {
    _.forOwn(current, (value, k) => {
      if (k === key) {
        result.push(value);
      }
      if (_.isObject(value)) {
        traverse(value);
      }
    });
  }

  traverse(obj);
  return result;
}

export function limitDecimals(x, decimals) {
  if (x === null || x === undefined) return "";
  if (x === 0) return x;
  return x.toFixed(decimals);
}

var Utils = {
  capitalize: function(s) {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  },

  dateFromTimestamp: function(timestamp) {
    return new firebase.firestore.Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
  },

  dateFromMillis: function(millis) {
    return firebase.firestore.Timestamp.fromMillis(millis).toDate();
  },

  spinner: function() {
    return (
      <div className="sk-circle">
        <div className="sk-circle1 sk-child"></div>
        <div className="sk-circle2 sk-child"></div>
        <div className="sk-circle3 sk-child"></div>
        <div className="sk-circle4 sk-child"></div>
        <div className="sk-circle5 sk-child"></div>
        <div className="sk-circle6 sk-child"></div>
        <div className="sk-circle7 sk-child"></div>
        <div className="sk-circle8 sk-child"></div>
        <div className="sk-circle9 sk-child"></div>
        <div className="sk-circle10 sk-child"></div>
        <div className="sk-circle11 sk-child"></div>
        <div className="sk-circle12 sk-child"></div>
      </div>
    );
  },

  // Generates a random id
  generateId: function() {
    let byteToHex = (byte) => {
      return ("0" + byte.toString(16)).slice(-2);
    };

    let len = 40;
    let arr = new Uint8Array(len / 2);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, byteToHex).join("");
  },

  // Returns a string where the occurence of the first letter is capitalized
  capitalizeFirstLetter: function(s) {
    if (!s) return null;
    if (s.length == 0) return s;
    let match = /[a-z]/i.exec(s);

    if (match) {
      return s.slice(0, match.index) + s[match.index].toUpperCase() + s.slice(match.index + 1, s.length);
    } else {
      return s;
    }
  },

  // strips an array of texts of empty lines
  stripEmptyLines: function(texts) {
    var out = [];

    texts.forEach((text) => {
      out.push(text.replace(/^\s*[\r\n]/gm, ""));
    });

    return out;
  },

  nl2br: function(string, presenter = null) {
    var output = string?.split("\n").map((item, i) => {
      return (
        <div key={i}>
          {presenter ? presenter(item, i) : item}
          <br />
        </div>
      );
    });

    return <div>{output}</div>;
  },
  nl2p: function(string) {
    var output = string.split("\n").map((item, i) => {
      return <p key={i}>{item}</p>;
    });

    return <div>{output}</div>;
  },

  nl2pRaw: function(string) {
    var output = string.split("\n").map((item, i) => {
      return (
        <p
          key={i}
          dangerouslySetInnerHTML={{ __html: item }}
        />
      );
    });

    return <div>{output}</div>;
  },

  numberWithCommas: (x) => {
    if (x === null || x === undefined) return "";
    if (x === 0) return x;
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  },

  isValidNum: (num) => {
    return !Number.isNaN(parseInt(num));
  },
};

export default Utils;
